import { useEffect, useRef, useState } from 'react';
import { Application, Report } from './types';
import { getCompletenessRating, getRiskLevel, RISK_SCALE_MAX } from './scoring';
import { formatDateTime } from './formatDateTime';

export interface CybersecurityAiAgentProps {
  app: Application;
  selectedReport: Report | null | undefined;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

/**
 * Cybersecurity AI Agent — floating chat widget, ported from
 * dummy_webpage/current/view2.js's four-layer design (UI / conversation
 * management / application-context provider / AI inference).
 *
 * Per explicit instruction: kept exactly as a mock for now — no real LLM
 * call. Layer 4 (requestAiResponse/generateMockAiReply) is the only piece
 * that would change when a real Bedrock-backed RAG pipeline is wired up
 * later; everything else (UI, conversation state, context snapshot) stays
 * structurally the same.
 */
export function CybersecurityAiAgent({
  app,
  selectedReport,
}: CybersecurityAiAgentProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return () => {};

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setIsMinimized(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  function getApplicationContext() {
    return {
      application: {
        name: app.name,
        ban: app.number,
        lifecycle: app.lifecycle,
        securityImpact: app.security_impact,
        businessUnit: app.business_unit,
        department: app.department,
        owner: app.owner,
      },
      questionnaire: { id: app.cyber_security_questionnaire_id || null },
      securitySummary: { link: app.latest_security_summary_link || null },
      selectedEvaluation: selectedReport
        ? {
            timestamp: selectedReport.timestamp,
            riskScore: selectedReport.system_risk_score,
            completenessScore: selectedReport.completeness_score,
            contentId: selectedReport.content_id,
            recommendations: selectedReport.recommendations,
            validationIssuesCount: selectedReport.validation_issues_count,
            crossSourceGapsCount: selectedReport.cross_source_gaps_count,
            traceabilityGapsCount: selectedReport.traceability_gaps_count,
            lifecycleGapsCount: selectedReport.lifecycle_gaps_count,
          }
        : null,
      evaluationHistoryCount: (app.reports || []).length,
    };
  }

  function generateMockAiReply(
    userText: string,
    context: ReturnType<typeof getApplicationContext>,
  ): string {
    const text = userText.toLowerCase();
    const evaluation = context.selectedEvaluation;

    if (/risk/.test(text)) {
      if (!evaluation) {
        return "There's no completed evaluation selected for this application yet, so I don't have a risk score to reference. Try requesting an evaluation from the Security Summary module first.";
      }
      const { label } = getRiskLevel(evaluation.riskScore as number);
      return `The most recent evaluation for ${
        context.application.name
      } reports a Risk Score of ${
        evaluation.riskScore
      } out of ${RISK_SCALE_MAX} (${label}), based on the report generated on ${formatDateTime(
        evaluation.timestamp,
      )}.`;
    }

    if (/complet/.test(text)) {
      if (!evaluation) {
        return "I don't have a completed evaluation to check completeness against yet for this application.";
      }
      const { rating } = getCompletenessRating(
        evaluation.completenessScore as number,
      );
      return `The Completeness Score for ${context.application.name} is ${
        evaluation.completenessScore
      }% (${rating}), from the evaluation dated ${formatDateTime(
        evaluation.timestamp,
      )}.`;
    }

    if (/recommend/.test(text)) {
      if (
        !evaluation ||
        !evaluation.recommendations ||
        evaluation.recommendations.length === 0
      ) {
        return "There aren't any recommendations recorded for the currently selected evaluation.";
      }
      const top = evaluation.recommendations.slice(0, 2);
      return `Here are the top recommendations from the latest evaluation:\n\u2022 ${top.join(
        '\n\u2022 ',
      )}`;
    }

    if (/gap|issue|finding/.test(text)) {
      if (!evaluation) {
        return "No completed evaluation is selected, so I don't have gap/issue counts to report yet.";
      }
      return `For the evaluation dated ${formatDateTime(
        evaluation.timestamp,
      )}: ${evaluation.validationIssuesCount ?? 0} validation issues, ${
        evaluation.crossSourceGapsCount ?? 0
      } cross-source gaps, ${
        evaluation.traceabilityGapsCount ?? 0
      } traceability gaps, and ${
        evaluation.lifecycleGapsCount ?? 0
      } lifecycle gaps.`;
    }

    if (/monitor|onboard/.test(text)) {
      return `Monitoring/onboarding status for ${context.application.name} is tracked in the Monitoring module — open it to see the current onboarding status and any pending security review.`;
    }

    if (/questionnaire/.test(text)) {
      return context.questionnaire.id
        ? `The Cybersecurity Initial Impact Questionnaire for ${context.application.name} is recorded under ID ${context.questionnaire.id}.`
        : 'No cybersecurity questionnaire is on file for this application yet.';
    }

    if (/auth(entic|oriz)/.test(text)) {
      return "Authentication and Authorization modules for this application don't have detailed data wired up yet — that's on the roadmap for this assistant.";
    }

    if (/supplier/.test(text)) {
      return "Supplier Agreements data isn't available for this application yet, but I'll be able to discuss supplier security reviews here once that module is populated.";
    }

    if (/hello|hi there|^hi\b|hey/.test(text)) {
      return `Hi again! What would you like to know about ${context.application.name}'s security posture?`;
    }

    return `I don't have a specific answer for that yet, but once I'm connected to the full evaluation and security data for ${context.application.name}, I'll be able to dig into that. In the meantime, try asking me about risk scores, completeness, recommendations, gaps, monitoring/onboarding status, or the initial questionnaire.`;
  }

  function requestAiResponse(userText: string): Promise<string> {
    return new Promise(resolve => {
      const thinkingDelay = 650 + Math.random() * 700;
      window.setTimeout(
        () => resolve(generateMockAiReply(userText, getApplicationContext())),
        thinkingDelay,
      );
    });
  }

  function buildWelcomeMessage(): string {
    return (
      `Hi, I'm the Cybersecurity AI Agent for ${app.name}. I can help with questions about this ` +
      `application's security assessments, risk findings, recommendations, monitoring status, onboarding ` +
      `guidance, compliance questions, authentication and authorization requirements, supplier security ` +
      `reviews, and overall security posture.\n\nWhat would you like to know?`
    );
  }

  function openChat() {
    setIsOpen(true);
    setIsMinimized(false);
    if (messages.length === 0) {
      setMessages([{ role: 'assistant', text: buildWelcomeMessage() }]);
    }
    window.setTimeout(() => inputRef.current?.focus(), 50);
  }

  function closeChat() {
    setIsOpen(false);
    setIsMinimized(false);
  }

  function toggleMinimize() {
    setIsMinimized(prev => {
      const next = !prev;
      if (!next) {
        window.setTimeout(() => inputRef.current?.focus(), 50);
      }
      return next;
    });
  }

  async function handleSend() {
    const text = inputValue.trim();
    if (!text || isThinking) return;

    setMessages(prev => [...prev, { role: 'user', text }]);
    setInputValue('');
    setIsThinking(true);

    const reply = await requestAiResponse(text);
    setIsThinking(false);
    setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  return (
    <>
      <button
        type="button"
        className={`ai-agent-launcher${isOpen ? ' is-hidden' : ''}`}
        aria-label="Open Cybersecurity AI Agent chat"
        onClick={openChat}
      >
        <span aria-hidden="true">&#128737;</span>
      </button>

      <section
        className={`ai-agent-panel${isOpen ? ' is-open' : ''}${
          isMinimized ? ' is-minimized' : ''
        }`}
        role="dialog"
        aria-label="Cybersecurity AI Agent chat"
        aria-hidden={!isOpen}
      >
        <header
          className="ai-agent-header"
          role="button"
          tabIndex={0}
          onClick={() => {
            if (isMinimized) toggleMinimize();
          }}
          onKeyDown={e => {
            if ((e.key === 'Enter' || e.key === ' ') && isMinimized) {
              e.preventDefault();
              toggleMinimize();
            }
          }}
        >
          <div className="ai-agent-header-title">
            <span className="ai-agent-header-icon" aria-hidden="true">
              &#128737;
            </span>
            <span>Cybersecurity AI Agent</span>
          </div>
          <div className="ai-agent-header-actions">
            <button
              type="button"
              className="ai-agent-icon-btn"
              aria-label="Minimize chat"
              onClick={e => {
                e.stopPropagation();
                toggleMinimize();
              }}
            >
              <span aria-hidden="true">&#8211;</span>
            </button>
            <button
              type="button"
              className="ai-agent-icon-btn"
              aria-label="Close chat"
              onClick={e => {
                e.stopPropagation();
                closeChat();
              }}
            >
              <span aria-hidden="true">&#10005;</span>
            </button>
          </div>
        </header>

        <div
          className="ai-agent-messages"
          ref={messagesRef}
          role="log"
          aria-live="polite"
        >
          {messages.map((m, i) => (
            <div
              key={i}
              className={`ai-agent-message ai-agent-message--${m.role}`}
              role="listitem"
            >
              {m.role === 'assistant' && (
                <span className="ai-agent-avatar" aria-hidden="true">
                  &#129302;
                </span>
              )}
              <div className="ai-agent-bubble">{m.text}</div>
            </div>
          ))}
          {isThinking && (
            <div className="ai-agent-message ai-agent-message--assistant">
              <span className="ai-agent-avatar" aria-hidden="true">
                &#129302;
              </span>
              <div className="ai-agent-bubble ai-agent-thinking-bubble">
                <span className="ai-agent-thinking-dot" />
                <span className="ai-agent-thinking-dot" />
                <span className="ai-agent-thinking-dot" />
              </div>
            </div>
          )}
        </div>

        <form
          className="ai-agent-input-row"
          onSubmit={e => {
            e.preventDefault();
            handleSend();
          }}
        >
          <input
            type="text"
            className="ai-agent-input"
            placeholder="Ask about this application's security posture..."
            autoComplete="off"
            aria-label="Message the Cybersecurity AI Agent"
            ref={inputRef}
            value={inputValue}
            disabled={isThinking}
            onChange={e => setInputValue(e.target.value)}
          />
          <button
            type="submit"
            className="ai-agent-send-btn"
            aria-label="Send message"
            disabled={isThinking}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M15 8L2 2l4 6-4 6 13-6z" fill="currentColor" />
            </svg>
          </button>
        </form>
      </section>
    </>
  );
}
